import { Box, Stack, VStack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import SearchBar from "components/SearchBar";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import CDS from "design-system";
import { InternalLink } from "components/Link";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "helpers/url";
import type { CustomForm } from "features/Resources/CustomForm/types";
import { NoTemplatesToShow } from "components/EmptyComponents/RandomEmptyState";
import { useQuery } from "@tanstack/react-query";
import { customFormKeys } from "features/Resources/CustomForm/hooks";
import { fetchCustomForms } from "features/Resources/CustomForm/api";
import { createQueryString } from "helpers/queryString";

const columnHelper = createColumnHelper<CustomForm>();

const columns = [
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    enableSorting: false,
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={generatePath({ path: PEOPLE_TEAM_ROUTES.customForms.show, params: { id: String(row.getValue().id) } })}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
        >
          <CDS.Text12 fontWeight="bold" wordBreak="break-word">
            {row.getValue().name}
          </CDS.Text12>
        </InternalLink>
      </Box>
    ),
    meta: {
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "description",
    header: "Instructions",
    enableSorting: false,
    cell: (row) => (
      <CDS.Text12 color="fg.subtle" wordBreak="break-word" noOfLines={2}>
        {row.getValue().descriptionPlain ?? ""}
      </CDS.Text12>
    ),
    meta: { headerHStackProps: { justifyContent: "flex-start" } },
  }),
  columnHelper.accessor((row) => row, {
    id: "fields",
    header: "Fields",
    enableSorting: false,
    cell: (row) => <CDS.Text12>{row.getValue().fieldsCount ?? 0}</CDS.Text12>,
    meta: {
      sx: { width: "100px", maxWidth: "100px", minWidth: "100px" },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
];

function FormsEmptyState() {
  const navigate = useNavigate();
  return (
    <VStack py={12} spacing={3} textAlign="center">
      <CDS.Text18 fontWeight="bold">No forms yet</CDS.Text18>
      <CDS.Text14 color="content.tertiary" maxW="440px">
        Custom forms let you collect structured information from new hires — college background,
        T-shirt size, dietary restrictions. Attach one to a complete-form task and it appears on
        their journey.
      </CDS.Text14>
      <CDS.Button
        intent={CDS.Button.intent.indigo}
        variant={CDS.Button.variant.solid}
        size="sm"
        onClick={() => navigate(PEOPLE_TEAM_ROUTES.customForms.new)}
      >
        Create your first form
      </CDS.Button>
    </VStack>
  );
}

export default function ListCustomForms() {
  const [searchText, setSearchText] = useState("");

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: 200 },
        additionalQueries: {
          q: searchText && searchText.length > 2 ? searchText : undefined,
        },
      }),
    [searchText],
  );

  const { data } = useQuery({
    queryKey: queryString ? [...customFormKeys.list(queryString)] : [...customFormKeys.lists()],
    queryFn: ({ signal }) => fetchCustomForms(queryString ? `?${queryString}` : "", signal),
    staleTime: 1000 * 60 * 3,
  });

  const forms = data?.data ?? [];

  return (
    <Stack direction="column" gap="4" w="100%">
      <Stack direction="row" w="100%" alignItems="center" justifyContent="flex-start">
        <SearchBar minWidth="500" onInput={setSearchText} />
      </Stack>
      <ControlledDataTable<CustomForm>
        data={forms}
        columns={columns}
        initialPagination={{ pageSize: 200, pageIndex: 0 }}
        emptyStateComponent={searchText.length > 2 ? <NoTemplatesToShow /> : <FormsEmptyState />}
      />
    </Stack>
  );
}
